import { useState } from 'react';
import { Icon } from '@iconify-icon/react';

interface VideoPlayerProps {
  src: string;
  title: string;
  /** Shown on the poster above the title. */
  eyebrow?: string;
  caption?: string;
  /**
   * Seconds to open at, skipping whatever precedes the talk (a waiting room,
   * people joining). The viewer can still scrub back — nothing is cut.
   */
  startAt?: number;
}

/**
 * Click-to-load player for a webinar recording. The <video> element is only
 * mounted once someone hits play, so the page never downloads the recording
 * (64 MB for session 1) just to show a poster.
 */
const VideoPlayer: React.FC<VideoPlayerProps> = ({ src, title, eyebrow, caption, startAt = 0 }) => {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <video
        // The media fragment makes the browser open at the offset before the
        // first frame paints; onLoadedMetadata covers the browsers that ignore
        // it, and re-seeking to the same second is a no-op where it worked.
        src={startAt > 0 ? `${src}#t=${startAt}` : src}
        title={title}
        controls
        autoPlay
        preload="auto"
        playsInline
        onLoadedMetadata={(playerEvent) => {
          const player = playerEvent.currentTarget;
          if (startAt > 0 && player.currentTime < startAt) player.currentTime = startAt;
        }}
        className="aspect-video w-full rounded-[28px] bg-black"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      aria-label={`Play ${title}`}
      className="group relative flex aspect-video w-full flex-col justify-end overflow-hidden rounded-[28px] bg-[#06264b] text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_15%,#0055af99,transparent_60%),radial-gradient(circle_at_15%_90%,#d4a84b33,transparent_55%)]"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:2.5rem_2.5rem]"
      />
      <span
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-primary-600 shadow-xl transition-transform duration-200 group-hover:scale-110">
        <Icon icon="tabler:player-play-filled" className="text-3xl" />
      </span>
      <span className="relative w-full bg-gradient-to-t from-[#001a33] via-[#001a33cc] to-transparent p-5 sm:p-6">
        {eyebrow && (
          <span className="block text-[11px] font-bold uppercase tracking-[0.2em] text-[#e6ba50]">
            {eyebrow}
          </span>
        )}
        <span className="mt-1.5 block text-xl font-semibold leading-snug text-white sm:text-2xl">
          {title}
        </span>
        {caption && (
          <span className="mt-1.5 block text-sm leading-relaxed text-[#a8d4f5]">{caption}</span>
        )}
      </span>
    </button>
  );
};

export default VideoPlayer;
